/**
 * Screen transitions and gestures — the difference between an app and a website in a phone frame.
 *
 * A web app that swaps screens instantly reads as a page load. A native app moves: the outgoing
 * screen slides left, the incoming one comes from the right, and going back reverses it. That single
 * cue is most of what people mean by "it feels native".
 *
 * Built on the View Transitions API where available (one line, GPU-composited, no library), with a
 * plain fallback everywhere else — so this never blocks a render on an unsupported browser.
 */

type NavigationDirection = 'forward' | 'back';

/**
 * Minimal shape of the API. Declared structurally rather than by extending Document: newer TS DOM
 * libs already type `startViewTransition`, and re-declaring it on Document conflicts with them.
 */
type StartViewTransition = (callback: () => void | Promise<void>) => { finished: Promise<void> };

const viewTransitionApi = (): StartViewTransition | null => {
	if (typeof document === 'undefined') return null;
	const candidate = (document as unknown as { startViewTransition?: unknown }).startViewTransition;
	return typeof candidate === 'function' ? (candidate as StartViewTransition).bind(document) : null;
};

/**
 * Run a navigation inside a directional view transition.
 *
 * The direction is stamped on the root element so CSS can pick the right pair of keyframes — the API
 * itself has no concept of forward or back, and without this every transition animates the same way,
 * which makes going back feel like going deeper.
 */
export function navigateWithTransition(direction: NavigationDirection, navigate: () => void): void {
	const prefersReducedMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
	const start = viewTransitionApi();
	if (!start || prefersReducedMotion) {
		navigate();
		return;
	}

	const root = document.documentElement;
	root.dataset.nav = direction;
	const transition = start(() => {
		navigate();
	});
	void transition.finished.finally(() => {
		delete root.dataset.nav;
	});
}

/**
 * Edge-swipe back, the gesture people reach for without being told it exists.
 *
 * Deliberately narrow: it only arms within `edgeWidth` of the left edge, because a swipe that starts
 * mid-screen belongs to whatever is under the finger (a carousel, a list row). Getting that wrong
 * makes an app feel like it is fighting you.
 *
 * Returns a cleanup function.
 */
export function attachEdgeSwipeBack(
	target: HTMLElement,
	onBack: () => void,
	{ edgeWidth = 28, threshold = 70 }: { edgeWidth?: number; threshold?: number } = {},
): () => void {
	let startX = 0;
	let startY = 0;
	let armed = false;

	const onStart = (e: TouchEvent) => {
		const touch = e.touches[0];
		if (!touch) return;
		armed = touch.clientX <= edgeWidth;
		startX = touch.clientX;
		startY = touch.clientY;
	};

	const onEnd = (e: TouchEvent) => {
		if (!armed) return;
		armed = false;
		const touch = e.changedTouches[0];
		if (!touch) return;
		const dx = touch.clientX - startX;
		const dy = Math.abs(touch.clientY - startY);
		// Horizontal intent only: a diagonal drag is a scroll that happened to start near the edge.
		if (dx > threshold && dy < dx * 0.6) onBack();
	};

	target.addEventListener('touchstart', onStart, { passive: true });
	target.addEventListener('touchend', onEnd, { passive: true });
	return () => {
		target.removeEventListener('touchstart', onStart);
		target.removeEventListener('touchend', onEnd);
	};
}
